/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Mantine
import { Title, Box } from '@mantine/core';
import { useForm } from '@mantine/form';

// Components
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';
import { TextInput } from '@components/atoms/TextInput';
import { Link } from '@components/atoms/Link';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Styles
import classes from './ForgotPassword.module.css';

// Fetch data
import { useForgotPassword } from '@features/auth/api/forgot-password';

// I18n
import { useTranslation } from 'react-i18next';

// Configs
import { REGEX_EMAIL } from '@config/constants/regex';

// Utils
import {
	capitalizeString,
	formatTime,
} from '@utils/functions';
import { useMediaQuery } from '@mantine/hooks';
import { useCountdown } from '@hooks/useCountDown';

// Store
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const ForgotPassword = (props: {
	handlenavigate: (_target: string) => void;
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { t } = useTranslation();

	const {
		isShowModalEmailSent,
		setIsShowModalEmailSent,
	} = useStore();

	const form = useForm({
		initialValues: {
			email: '',
		},
		validate: {
			email: (value) =>
				REGEX_EMAIL.test(value)
					? null
					: 'form.error.invalidEmail',
		},
	});

	const { timeLeft, startCountdown } =
		useCountdown(30000);

	const forgotPassword = useForgotPassword({
		mutationConfig: {
			onSuccess() {
				setIsShowModalEmailSent(true);
				startCountdown();
			},
		},
	});

	/* ******************************** HELPERS ******************************* */
	const handlereturn = () => {
		props.handlenavigate('signin');
	};

	/* ******************************** CONSTANTS ******************************* */
	const hasTimeLeft = timeLeft !== null;

	/* ******************************** RENDERING ******************************* */
	return (
		<form
			onSubmit={form.onSubmit((values) => {
				forgotPassword.mutate(values);
			})}
		>
			<Box className={classes.containerForm}>
				<DefaultModal
					type="information"
					opened={isShowModalEmailSent}
					onConfirm={() => {}}
					onClose={() => setIsShowModalEmailSent(false)}
					title={t('forgotPassword.emailSentMessage')}
				/>
				<Title hiddenFrom="xxl" size="h4">
					{t('forgotPassword.title')}
				</Title>
				<Title visibleFrom="xxl" size="h3">
					{t('forgotPassword.title')}
				</Title>
				<Text
					hiddenFrom="xxl"
					type="body2"
					color="inputs-texts-icons"
				>
					{t('forgotPassword.recoverPassword')}
				</Text>
				<Text
					visibleFrom="xxl"
					type="body1"
					color="inputs-texts-icons"
				>
					{t('forgotPassword.recoverPassword')}
				</Text>
				<Box className={classes.spacerVertical32} />
				<TextInput
					size="lg"
					hiddenFrom="xxl"
					radius="8.29px"
					disabled={hasTimeLeft}
					placeholder={t('auth.yourEmailAddress')}
					{...form.getInputProps('email')}
				/>
				<TextInput
					size="xl"
					visibleFrom="xxl"
					radius="8.29px"
					disabled={hasTimeLeft}
					placeholder={t('auth.yourEmailAddress')}
					{...form.getInputProps('email')}
				/>
				{hasTimeLeft && (
					<Text type="body2" color="inputs-texts-icons">
						{t('forgotPassword.timerText') +
							` ${formatTime(timeLeft)} `}
					</Text>
				)}
				<Box className={classes.spacerVertical24} />

				<Button
					size={isBigScreen ? 'lg' : 'md'}
					fullWidth
					type="submit"
					disabled={hasTimeLeft}
					loading={
						forgotPassword && forgotPassword.isLoading
					}
				>
					{capitalizeString(
						t('forgotPassword.sentResetLink')
					)}
				</Button>
				<Box className={classes.containerReturnButton}>
					<Link
						onClick={handlereturn}
						color="blue"
						underline="never"
					>
						{capitalizeString(
							t('forgotPassword.backToLogin')
						)}
					</Link>
				</Box>
			</Box>
		</form>
	);
};
